<template>
    <div :class="classes">
        <swiper
            ref="swiper"
            :options="swiperOptions"
            @ready="onReady"
            @tap="onTap"
            @resize="onResize"
            @slide-change="onSlideChange"
        >
            <!-- @slot default slot for DkSlider component -->
            <slot></slot>
        </swiper>

        <!-- Optional controls -->
        <div v-if="areControlsVisible" class="dk-slider__controls" :class="controlsClasses">
            <div
                slot="pagination"
                :class="[
                    `swiper-pagination pagination-${_uid}`,
                    { 'tw-pointer-events-none tw-cursor-default': areBulletsDisabled },
                ]"
            ></div>
            <slot v-if="areButtonsVisible" name="button-prev" v-bind="{ _uid }">
                <div :class="`swiper-button-prev prev-${_uid} u-hidden-till--medium`">
                    <button
                        class="tw-inline-flex tw-justify-center tw-items-center tw-w-12 tw-h-12 tw-border tw-border-border-color tw-border-solid tw-bg-white tw-outline-none tw-cursor-pointer hover:tw-text-slate-500"
                    >
                        <dk-icon name="chevron-left" size="s" />
                    </button>
                </div>
            </slot>
            <slot v-if="areButtonsVisible" name="button-next" v-bind="{ _uid }">
                <div :class="`swiper-button-next next-${_uid} u-hidden-till--medium`">
                    <button
                        class="tw-inline-flex tw-justify-center tw-items-center tw-w-12 tw-h-12 tw-border tw-border-border-color tw-border-solid tw-bg-white tw-outline-none tw-cursor-pointer hover:tw-text-slate-500"
                    >
                        <dk-icon name="chevron-right" size="s" />
                    </button>
                </div>
            </slot>
            <slot name="scrollbar" v-bind="{ _uid }">
                <div :class="`swiper-scrollbar scrollbar-${_uid}`"></div>
            </slot>
        </div>
    </div>
</template>

<script>
import merge from 'lodash.merge';
import debounce from 'lodash/debounce';
import { isMobile } from '@v4/common/utils/media-query';
import { swiper, swiperSlide } from 'vue-awesome-swiper';

/**
 * A slider component which is wrapping Swiper Slider.
 *
 * @version 4.0.0
 * @see [Vue-Awesome-Swiper](https://github.com/surmon-china/vue-awesome-swiper)
 * @see [Swiper API](https://github.com/nolimits4web/Swiper/blob/Swiper4/API.md)
 * @example ./../examples/DkSlider.md
 */
export default {
    name: 'DkSliderDST',
    components: {
        swiper,
        // eslint-disable-next-line vue/no-unused-components
        swiperSlide,
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
    },
    props: {
        /**
         * Theme name.
         */
        theme: {
            type: String,
            default: () => 'default',
        },
        /**
         * Custom options.
         */
        options: {
            type: Object,
            default: () => ({}),
        },
        /**
         * @see [Swiper Parameters](http://idangero.us/swiper/api/#parameters).
         */
        swiperOpts: {
            type: Object,
            default: () => ({}),
        },
        areControlsVisible: {
            type: Boolean,
            default: true,
        },
        areButtonsVisible: {
            type: Boolean,
            default: true,
        },
        sliderControlsClasses: {
            type: String,
            default: '',
        },
        areBulletsDisabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            classes: [
                'dk-slider-dst',
                `dk-slider-dst--${this.theme}`,
                this.options.clickable ? 'dk-slider--clickable' : '',
            ],
        };
    },
    computed: {
        swiper() {
            return this.$refs.swiper.swiper;
        },
        opts() {
            return merge(
                {
                    pagination: true,
                    navigation: true,
                    scrollbar: false,
                },
                this.options
            );
        },
        swiperOptions() {
            return merge(
                {
                    slidesPerView: 3,
                    pagination: {
                        el: this.opts.pagination ? `.swiper-pagination.pagination-${this._uid}` : null,
                        clickable: true,
                    },
                    navigation: {
                        nextEl: this.sliderControlsClasses
                            ? `.swiper-custom-button-next.next-${this._uid}`
                            : this.opts.navigation
                                ? `.swiper-button-next.next-${this._uid}`
                                : null,
                        prevEl: this.sliderControlsClasses
                            ? `.swiper-custom-button-prev.prev-${this._uid}`
                            : this.opts.navigation
                                ? `.swiper-button-prev.prev-${this._uid}`
                                : null,
                    },
                    scrollbar: {
                        el: this.opts.scrollbar ? `.swiper-scrollbar.scrollbar-${this._uid}` : null,
                    },
                    breakpoints: {
                        767: {
                            slidesPerView: 1,
                        },
                    },
                },
                this.swiperOpts
            );
        },
        controlsClasses() {
            return this.sliderControlsClasses || 'tw-relative tw-mt-6';
        },
    },
    methods: {
        /**
         * Allows the slides to slide. Dipending on device and on slide numbers.
         *
         * @return {void}
         */
        allowSlide() {
            const slides = this.swiper.slides.length;

            if (!slides) {
                return;
            }

            if (
                (!isMobile() && (slides === 1 || slides <= this.swiperOptions.slidesPerView)) ||
                (isMobile() && slides === 1)
            ) {
                this.swiper.allowSlidePrev = false;
                this.swiper.allowSlideNext = false;
            }
        },
        onReady() {
            /**
             * Fired when swiper slider is ready.
             *
             * @event ready
             * @type {object} Swiper instance
             */
            this.$emit('ready', this.swiper);
            const slides = this.swiper.slides.length;

            if (slides === 1) {
                this.classes.push('has-one');
            } else if (slides <= this.swiperOptions.slidesPerView) {
                this.classes.push('has-less-then-default');
            }

            this.allowSlide();
        },
        /**
         * Using debounce in order to avoid emitting events multipe times.
         *
         * @see {@link https://github.com/surmon-china/vue-awesome-swiper/issues/286| Click event emit twice}
         * @return {void}
         */
        onTap: debounce(function($event) {
            let { clickedIndex } = this.swiper;
            const { clickedSlide } = this.swiper;

            if (clickedSlide) {
                const slideIndex = clickedSlide.getAttribute('data-swiper-slide-index');

                if (this.swiperOptions.loop && slideIndex) {
                    clickedIndex = parseInt(slideIndex, 10);
                }

                /**
                 * Fired when a specific slide is clicked.
                 *
                 * @event clickedSlide
                 * @type {object}
                 * @property {number} clickedIndex Slide index
                 * @property {object} clickedSlide Slide HTML element
                 * @property {object} $event PointerEvent
                 */
                this.$emit('clicked-slide', { clickedIndex, clickedSlide, $event });
            }
        }),
        /**
         * Using debounce in order to avoid emitting events multipe times.
         *
         * @see {@link https://github.com/surmon-china/vue-awesome-swiper/issues/286| Click event emit twice}
         * @return {void}
         */
        onResize: debounce(function() {
            this.allowSlide();
        }),
        /**
         * Triggered on slide change
         *
         * @return {void}
         */
        onSlideChange() {
            this.$emit('slide-change');
        },
    },
};
</script>

<style lang="scss">
$themeColor: $color__primary;

@import '~swiper/src/swiper';
@import '~swiper/src/components/lazy/lazy';
@import '~swiper/src/components/navigation/navigation';
@import '~swiper/src/components/pagination/pagination';

.dk-slider-dst {
    $container: &;

    position: relative;

    .swiper-pagination-bullets {
        width: 100%;
    }

    .swiper-button-prev > .btn,
    .swiper-button-next > .btn {
        display: none;
    }

    &.has-one &__controls {
        display: none;
    }

    &--clickable {
        .swiper-slide {
            cursor: pointer;
        }
    }

    // Default theme
    &--default {
        $controls-margin: $spacing__l;

        #{$container}__controls {
            margin-top: $controls-margin;
            text-align: center;
        }

        &:hover {
            .swiper-button-next,
            .swiper-button-prev {
                display: block;

                &.swiper-button-disabled {
                    pointer-events: auto;
                }
            }
        }

        .swiper-button-next,
        .swiper-button-prev {
            bottom: 0;
            transform: translateY(50%);

            display: block;
            margin-top: 0;
            height: 40px;
            width: 40px;

            background-image: none;

            button {
                width: 100%;
                height: 100%;
            }
        }

        .swiper-button-prev {
            left: 0;
        }

        .swiper-button-next {
            right: 0;
        }

        .swiper-pagination {
            position: static;
            margin-bottom: -0.75rem;
            margin-top: -0.75rem;
        }

        .swiper-pagination-bullet-active {
            background: $color__primary;
        }

        .swiper-pagination {
            & ~ .swiper-button-prev,
            & ~ .swiper-button-next {
                margin-top: -#{($icon-width__base + $controls-margin + 8) / 2};
            }
        }
    }

    @include vp-medium {
        &.has-less-then-default &__controls {
            display: none;
        }
    }

    .swiper-pagination-bullet {
        @apply tw-border tw-border-solid tw-border-slate-500 tw-rounded-full tw-bg-transparent tw-opacity-100;
        width: 10px;
        height: 10px;
        margin: 0.75rem 0.5rem;
    }

    .swiper-pagination-bullet-active {
        @apply tw-bg-brand-primary tw-border-brand-primary;
    }
}

.dk-search-vehicle-card__media-container .swiper-container {
    height: 100%;
}
</style>
